import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Observes the header until the app block source has hydrated, so it can be moved into the drawer target.
   * @type {MutationObserver | null}
   */
  #appBlocksObserver = null;

  /**
   * Debounce timeout ID that coalesces bursts of mutations into a single move check.
   * @type {number | null}
   */
  #appBlocksMoveTimeout = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.#setStickyState(isIntersecting ? 'inactive' : 'active');
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Sets the sticky state on both the header component and the document body,
   * so other components can style based on the header's sticky state.
   * @param {string} value
   */
  #setStickyState = (value) => {
    this.dataset.stickyState = value;
    document.body.dataset.stickyState = value;
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.#setStickyState('inactive');
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.#setStickyState('active');
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.#setStickyState('idle');
    } else {
      this.dataset.scrollDirection = 'none';
      this.#setStickyState('idle');
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Moves the single rendered header app block into the drawer target, once the app has hydrated it.
   * This avoids duplicate app block IDs while allowing app JS time to hydrate the source block.
   *
   * The source and target containers carry `data-skip-node-update`/`data-skip-subtree-update`
   * (see sections/header.liquid and snippets/header-drawer.liquid) so that later section morphs —
   * the header's own one-time hydration pass, and the full header re-render triggered on every
   * add-to-cart by component-cart-items.js — leave the moved markup alone. We only empty the
   * source container rather than removing it, so its wrapper element stays a stable sibling for
   * those future morphs instead of leaving a gap that could get matched against the wrong node.
   * @returns {boolean} Whether the app block is in its final position in the drawer target.
   */
  #moveHeaderAppBlocksToDrawer = () => {
    const source = this.querySelector('[data-header-app-blocks-source]');
    const target = this.querySelector('[data-header-app-blocks-target]');

    if (!target) return true;
    if (!source) return Boolean(target.querySelector('.shopify-app-block'));

    if (target.querySelector('.shopify-app-block')) {
      target.setAttribute('data-header-app-blocks-ready', 'true');
      source.replaceChildren();
      return true;
    }

    if (!source.querySelector('.shopify-app-block') || !source.querySelector('.gls-market-container')) return false;

    target.append(...source.childNodes);
    target.setAttribute('data-header-app-blocks-ready', 'true');

    return true;
  };

  /**
   * Debounces bursts of mutations into a single move check, disconnecting the observer once the
   * app block has reached its final position in the drawer target.
   */
  #scheduleHeaderAppBlocksMove = () => {
    if (!this.#appBlocksObserver || this.#appBlocksMoveTimeout) return;

    this.#appBlocksMoveTimeout = window.setTimeout(() => {
      this.#appBlocksMoveTimeout = null;

      if (!this.#moveHeaderAppBlocksToDrawer()) return;

      this.#appBlocksObserver?.disconnect();
      this.#appBlocksObserver = null;
    }, 50);
  };

  /**
   * Starts observing header DOM changes so the app block can be moved as soon as the app hydrates it.
   * Keeps observing indefinitely (childList for newly inserted markup, attributes for class-only
   * changes) since third-party app hydration time is unpredictable.
   */
  #observeHeaderAppBlocks = () => {
    if (this.#moveHeaderAppBlocksToDrawer()) return;

    this.#appBlocksObserver = new MutationObserver(() => {
      this.#scheduleHeaderAppBlocksMove();
    });

    this.#appBlocksObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.addEventListener('toggle', this.#scheduleHeaderAppBlocksMove, true);
    window.addEventListener('load', this.#scheduleHeaderAppBlocksMove);
    this.#observeHeaderAppBlocks();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#appBlocksObserver?.disconnect();
    this.#appBlocksObserver = null;
    if (this.#appBlocksMoveTimeout) {
      clearTimeout(this.#appBlocksMoveTimeout);
      this.#appBlocksMoveTimeout = null;
    }
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('toggle', this.#scheduleHeaderAppBlocksMove, true);
    window.removeEventListener('load', this.#scheduleHeaderAppBlocksMove);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
